import { Button, Modal, Select, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import {
  userManagement,
  type FirmEntitlementRow,
  type FirmPublicationRestrictionRow,
} from "../../../api/userManagement.service";
import { JurisdictionPicker } from "../../../components/JurisdictionPicker/JurisdictionPicker";
import { useJurisdictionOptions } from "../../../hooks/useJurisdictionOptions";
import LoadingState from "../../../components/LoadingState";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import styles from "../UserManagement.module.css";

const FIRM_ENTITLEMENT_PRODUCTS = [
  { key: "live", label: "Legal 500 Live" },
  { key: "sars", label: "Submissions Analysis (SARS)" },
  { key: "referee_track", label: "Referee Track & Add" },
  { key: "historical_data", label: "Historical Data" },
];

const PHASES = [
  { value: "current", label: "Current" },
  { value: "next", label: "Next" },
];

type AddProductModalProps = {
  onAdd: (row: { product: string; regionId: string; phase: string }) => Promise<void>;
  onClose: () => void;
};

function AddProductModal({ onAdd, onClose }: AddProductModalProps) {
  const [selectedProduct, setSelectedProduct] = useState(FIRM_ENTITLEMENT_PRODUCTS[0].key);
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState("");
  const [selectedPhase, setSelectedPhase] = useState("current");
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit() {
    if (!selectedJurisdictionId) return;
    setIsSaving(true);
    try {
      await onAdd({
        product: selectedProduct,
        regionId: selectedJurisdictionId,
        phase: selectedPhase,
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal isOpen title="Add product" onClose={onClose} size="small">
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
        <Select
          label="Product"
          value={selectedProduct}
          onChange={event => setSelectedProduct(event.target.value)}
          options={FIRM_ENTITLEMENT_PRODUCTS.map(p => ({ value: p.key, label: p.label }))}
          disabled={isSaving}
        />
        <JurisdictionPicker
          value={selectedJurisdictionId}
          onChange={setSelectedJurisdictionId}
          disabled={isSaving}
        />
        <Select
          label="Publication Edition"
          value={selectedPhase}
          onChange={event => setSelectedPhase(event.target.value)}
          options={PHASES}
          disabled={isSaving}
        />
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleSubmit()}
            disabled={isSaving || !selectedJurisdictionId}
          >
            {isSaving ? "Adding…" : "Add"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function productLabel(productKey: string): string {
  return FIRM_ENTITLEMENT_PRODUCTS.find(p => p.key === productKey)?.label ?? productKey;
}

function sourceBadge(source: "reconcile" | "manual") {
  if (source === "reconcile") {
    return (
      <span
        className={`${styles.statusBadge} ${styles.statusAuto}`}
        title="Derived from the data-mart / legacy / UPE by the entitlement reconcile"
      >
        Data-mart / automated
      </span>
    );
  }
  return (
    <span
      className={`${styles.statusBadge} ${styles.statusManual}`}
      title="Set by staff; protected from the reconcile"
    >
      Manually set
    </span>
  );
}

type FirmPublicationRestrictionSubsectionProps = {
  rows: FirmPublicationRestrictionRow[];
  labelByJurisdictionId: Map<string, string>;
};

function FirmPublicationRestrictionSubsection({
  rows,
  labelByJurisdictionId,
}: FirmPublicationRestrictionSubsectionProps) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-sm)" }}>
      <thead>
        <tr>
          {["Region", "Publication Edition", "Source"].map(header => (
            <th
              key={header}
              style={{
                textAlign: "left",
                padding: "0.5rem 0.75rem",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-text-secondary)",
                borderBottom: "1px solid var(--color-border)",
              }}
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr
            key={`${row.regionId}-${row.phase}-${rowIndex}`}
            style={{ opacity: row.source === "reconcile" ? 0.75 : 1 }}
          >
            <td
              style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--color-border)" }}
            >
              {labelByJurisdictionId.get(row.regionId) ?? row.regionId}
            </td>
            <td
              style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--color-border)" }}
            >
              {row.phase}
            </td>
            <td
              style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--color-border)" }}
            >
              {sourceBadge(row.source)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type FirmEntitlementSectionProps = {
  orgId: string;
};

export function FirmEntitlementSection({ orgId }: FirmEntitlementSectionProps) {
  const { allOptions: jurisdictionOptions } = useJurisdictionOptions();
  const labelByJurisdictionId = new Map(jurisdictionOptions.map(opt => [opt.value, opt.label]));

  const [entitlementRows, setEntitlementRows] = useState<FirmEntitlementRow[]>([]);
  const [publicationRestriction, setPublicationRestriction] = useState<
    FirmPublicationRestrictionRow[] | null
  >(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    void loadData();
  }, [orgId]);

  async function loadData() {
    setIsLoading(true);
    try {
      const [fetchedRows, restrictionResult] = await Promise.allSettled([
        userManagement.getFirmEntitlement(orgId),
        userManagement.getFirmPublicationRestriction(orgId),
      ]);
      if (fetchedRows.status === "fulfilled") setEntitlementRows(fetchedRows.value);
      if (restrictionResult.status === "fulfilled") {
        setPublicationRestriction(
          restrictionResult.value.length > 0 ? restrictionResult.value : null,
        );
      } else {
        setPublicationRestriction(null);
      }
      if (fetchedRows.status === "rejected") {
        setToast({
          message: `Failed to load firm entitlement: ${fetchedRows.reason instanceof Error ? fetchedRows.reason.message : "Unknown error"}`,
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleAddRow(row: { product: string; regionId: string; phase: string }) {
    try {
      await userManagement.addFirmEntitlementRow(orgId, row);
      const refreshed = await userManagement.getFirmEntitlement(orgId);
      setEntitlementRows(refreshed);
      setToast({ message: "Manual entitlement row added", type: "success" });
    } catch (err) {
      setToast({
        message: `Failed to add row: ${err instanceof Error ? err.message : "Unknown error"}`,
        type: "error",
      });
    }
  }

  return (
    <div className={styles.section}>
      <h2
        className={styles.sectionTitle}
        style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}
      >
        Firm Products
        <span
          style={{
            fontSize: "var(--font-size-xs, 0.75rem)",
            fontWeight: "var(--font-weight-medium, 500)",
            color: "var(--color-warning, #b45309)",
            border: "1px dashed var(--color-warning, #b45309)",
            borderRadius: "999px",
            padding: "0.125rem 0.625rem",
          }}
        >
          Interim — moves to Fulfilment
        </span>
      </h2>

      {isLoading ? (
        <LoadingState />
      ) : (
        <>
          {entitlementRows.length === 0 ? (
            <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
              No firm entitlements. Derived rows appear after the next reconcile run.
            </p>
          ) : (
            <table
              style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-sm)" }}
            >
              <thead>
                <tr>
                  {["Product", "Region", "Publication Edition", "Source"].map(header => (
                    <th
                      key={header}
                      style={{
                        textAlign: "left",
                        padding: "0.5rem 0.75rem",
                        fontWeight: "var(--font-weight-semibold)",
                        color: "var(--color-text-secondary)",
                        borderBottom: "1px solid var(--color-border)",
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entitlementRows.map((row, rowIndex) => (
                  <tr
                    key={`${row.product}-${row.regionId}-${row.phase}-${rowIndex}`}
                    style={{
                      opacity: row.source === "reconcile" ? 0.75 : 1,
                    }}
                  >
                    <td
                      style={{
                        padding: "0.5rem 0.75rem",
                        borderBottom: "1px solid var(--color-border)",
                      }}
                    >
                      {productLabel(row.product)}
                    </td>
                    <td
                      style={{
                        padding: "0.5rem 0.75rem",
                        borderBottom: "1px solid var(--color-border)",
                      }}
                    >
                      {labelByJurisdictionId.get(row.regionId) ?? row.regionId}
                    </td>
                    <td
                      style={{
                        padding: "0.5rem 0.75rem",
                        borderBottom: "1px solid var(--color-border)",
                      }}
                    >
                      {row.phase}
                    </td>
                    <td
                      style={{
                        padding: "0.5rem 0.75rem",
                        borderBottom: "1px solid var(--color-border)",
                      }}
                    >
                      {sourceBadge(row.source)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <PermissionGate permission="user.write_accounts">
            <div style={{ marginTop: "1rem" }}>
              <Button
                variant="outlined"
                color="default"
                size="small"
                onClick={() => setShowAddModal(true)}
              >
                Add product
              </Button>
            </div>
          </PermissionGate>

          {showAddModal && (
            <AddProductModal onAdd={handleAddRow} onClose={() => setShowAddModal(false)} />
          )}

          {publicationRestriction !== null && (
            <div style={{ marginTop: "1.5rem" }}>
              <h3
                style={{
                  fontSize: "var(--font-size-base)",
                  fontWeight: "var(--font-weight-semibold)",
                  color: "var(--color-text-primary)",
                  marginBottom: "0.75rem",
                }}
              >
                Publication restriction
              </h3>
              <FirmPublicationRestrictionSubsection
                rows={publicationRestriction}
                labelByJurisdictionId={labelByJurisdictionId}
              />
            </div>
          )}
        </>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default FirmEntitlementSection;
